import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as requests from './requests'
import { TSupportConversation } from './types'

export const KEY_SUPPORT_CONVERSATION = 'support-conversation'

/**
 * Abre a conversa do pedido.
 *
 * `enabled` pelo orderId: o painel só chama quando o paciente clica em
 * "Pedir ajuda" — abrir a tela de pedidos não cria conversa nenhuma.
 *
 * Os refetch automáticos ficam TODOS desligados de propósito. A `queryFn` é um
 * POST que **cria** a conversa quando não existe uma aberta: qualquer refetch
 * espontâneo — trocar de aba, reconectar, remontar o componente — abriria um
 * atendimento novo sem ninguém ter pedido. Foi exatamente isso que apagava a
 * pesquisa de satisfação e devolvia o paciente ao menu ao encerrar.
 */
export const useOpenConversation = (orderId?: number | null) =>
  useQuery({
    queryKey: [KEY_SUPPORT_CONVERSATION, orderId],
    queryFn: () => requests.openConversation(orderId as number),
    enabled: !!orderId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false
  })

/**
 * Estado da conversa e posição na fila.
 *
 * As mensagens vêm do Realtime Database, mas a fila vive no MySQL — por isso
 * este polling. 10s é suficiente para a posição andar de forma perceptível.
 */
export const useConversationStatus = (uuid?: string | null, isQueued = false) =>
  useQuery({
    queryKey: [KEY_SUPPORT_CONVERSATION, 'status', uuid],
    queryFn: () => requests.getConversation(uuid as string),
    enabled: !!uuid,
    refetchInterval: isQueued ? 1000 * 10 : false
  })

/**
 * Aplica no cache o novo estado da conversa, em vez de invalidar.
 *
 * Invalidar aqui refaria o POST de abertura e criaria outra conversa. Como as
 * mutações já devolvem o registro atualizado, escrever direto no cache é ao
 * mesmo tempo mais correto e mais barato.
 */
const patchConversationCache = (queryClient: QueryClient, uuid: string, patch: Partial<TSupportConversation>) => {
  queryClient.setQueriesData({ queryKey: [KEY_SUPPORT_CONVERSATION] }, (old: unknown) => {
    if (!old || typeof old !== 'object') return old

    // Query de abertura: { conversation, firebase_token }
    if ('conversation' in old) {
      const opened = old as { conversation: TSupportConversation }

      if (opened.conversation?.uuid !== uuid) return old

      return { ...opened, conversation: { ...opened.conversation, ...patch } }
    }

    // Query de status: a própria conversa
    const conversation = old as TSupportConversation

    if (conversation.uuid !== uuid) return old

    return { ...conversation, ...patch }
  })
}

export const useRequestHuman = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (uuid: string) => requests.requestHuman(uuid),
    onSuccess: (updated) => patchConversationCache(queryClient, updated.uuid, updated)
  })
}

export const useCloseConversation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (uuid: string) => requests.closeConversation(uuid),
    onSuccess: (updated) => patchConversationCache(queryClient, updated.uuid, updated)
  })
}

/**
 * Envia a mensagem do paciente.
 *
 * Não mexe no cache de mensagens de propósito: a mensagem enviada volta pela
 * escuta do Realtime Database, que é a única fonte da conversa na tela.
 */
export const useSendMessage = () =>
  useMutation({
    mutationFn: requests.sendMessage
  })

export const useUploadAttachment = () =>
  useMutation({
    mutationFn: requests.uploadAttachment
  })

export const useRateConversation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: requests.rateConversation,
    onSuccess: (result, variables) =>
      patchConversationCache(queryClient, variables.uuid, {
        nps_score: result.nps_score,
        nps_answered_at: result.nps_answered_at
      })
  })
}

export const KEY_CANCELLATION_REASONS = 'support-cancellation-reasons'

/** Lista quase imutável: cache longo, sem refetch. */
export const useCancellationReasons = () =>
  useQuery({
    queryKey: [KEY_CANCELLATION_REASONS],
    queryFn: requests.listCancellationReasons,
    staleTime: 1000 * 60 * 60
  })

export const KEY_MY_TICKETS = 'my-support-tickets'

export const useMyTickets = () =>
  useQuery({ queryKey: [KEY_MY_TICKETS], queryFn: requests.listMyTickets, staleTime: 1000 * 30 })

export const useMyTicket = (uuid: string | null) =>
  useQuery({
    queryKey: [KEY_MY_TICKETS, uuid],
    queryFn: () => requests.getMyTicket(uuid as string),
    enabled: !!uuid
  })

export const KEY_MY_CONVERSATIONS = 'support-my-conversations'

/**
 * Não lidas por pedido, para "Meus Pedidos". Relê a cada 30s e ao voltar à
 * aba — é o que faz a marca aparecer quando a equipe responde.
 */
export const useMyConversationsUnread = () =>
  useQuery({
    queryKey: [KEY_MY_CONVERSATIONS],
    queryFn: requests.listMyConversations,
    staleTime: 1000 * 15,
    refetchInterval: 1000 * 30,
    select: (rows) =>
      Object.fromEntries(
        rows.filter((row) => row.unread_count > 0).map((row) => [row.order_id, row.unread_count])
      ) as Record<number, number>
  })

/** Com a conversa na tela: marca como lida e atualiza a marca em "Meus Pedidos". */
export const useMarkConversationRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: requests.markConversationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY_MY_CONVERSATIONS] })
  })
}

export const useMarkTicketRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: requests.markTicketRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY_MY_TICKETS] })
  })
}
